import { useEffect } from "react";

// External dependencies
import { __ } from "@wordpress/i18n";
import { registerBlockType } from "@wordpress/blocks";
import { Fragment } from "@wordpress/element";
import { compose } from '@wordpress/compose';
import { withSelect, withDispatch } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import { store as editorStore } from "@wordpress/editor";
import { RichText, BlockControls, MediaReplaceFlow } from "@wordpress/block-editor";
import Button from '../components/Button/Button.edit'

// Internal dependencies
import ResourceHeader from "../components/Resources/ResourcesHeader";

// Block attributes (custom props)
const attributes = {
    // BG Image attributes
    image: {
        type: 'object',
        default: {}
    },
    headline: {
        type: 'string',
        default: ""
    },
    cta: {
        type: 'object',
        default: null
    }
}


const applyWithSelect = withSelect((select) => {
    const { getMedia } = select(coreStore);
    const { getEditedPostAttribute } = select(editorStore);
    const featuredImageId = getEditedPostAttribute('featured_media');
    const featuredImage = featuredImageId ? getMedia(featuredImageId, { context: 'view' }) : null;

    return {
        featuredImage: featuredImage ? { id: featuredImage?.id, url: featuredImage?.media_details?.sizes?.full?.source_url } : null,
        postTitle: getEditedPostAttribute('title'),
    };
})

const applyWithDispatch = withDispatch(
    (dispatch) => {
        const { editPost } = dispatch(editorStore);
        return {
            updateFeaturedImage(image) {
                editPost({ featured_media: image.id });
            },
            updatePostTitle(title) {
                editPost({ title });
            },
        };
    }
);

// Edit function (responsible for wp-admin post edit screen)
const edit = compose([applyWithSelect, applyWithDispatch])(
    ({ setAttributes, attributes, postTitle, featuredImage, ...props }) => {
        const { headline, image, cta } = attributes;

        useEffect(() => {
            if (image?.id !== featuredImage?.id) {
                setAttributes({ image: featuredImage });
            }
        }, [image, featuredImage])

        useEffect(() => {
            if (headline !== postTitle) {
                setAttributes({ headline: postTitle });
            }
        }, [headline, postTitle])

        return (
            <Fragment>
                <BlockControls group="other">
                    <MediaReplaceFlow
                        mediaId={image?.id}
                        allowedTypes={['image']}
                        accept="image/*"
                        onSelect={(media) => props.updateFeaturedImage(media)}
                    />
                </BlockControls>
                <ResourceHeader
                    style={{ width: '100%', marginLeft: 0 }}
                    image={image?.url}
                    headline={
                        <RichText
                            value={headline}
                            multiline={false}
                            onChange={(headline) => props.updatePostTitle(headline)}
                            placeholder={__('Hero Headline', 'hero')}
                            allowedFormats={[]}
                            keepPlaceholderOnFocus={true}
                        />}
                    cta={<Button value={cta} onChange={(cta) => setAttributes({ cta })} />}
                />
            </Fragment>
        );
    }
)

// Save function (responsible for static HTML saved in database)
const save = ({ attributes }) => {
    const { image = {}, headline, cta } = attributes;

    return (
        <div data-mt-attributes={encodeURIComponent(JSON.stringify(attributes))}>
            {image?.id ? <ResourceHeader
                image={image.url}
                headline={<RichText.Content value={headline} />}
                cta={<Button.Content value={cta} />}
            /> : null}
        </div>
    );
}

/**
 * Every block starts by registering a new block type definition.
 *
 * @see https://developer.wordpress.org/block-editor/developers/block-api/#registering-a-block
 */
registerBlockType("mt/gutenberg-resource-hero", {

    /**
     * This is the display title for your block, which can be translated with `i18n` functions.
     * The block inserter will show this name.
     */
    title: __("Resource Hero", "gutenberg-resource-hero"),

    /**
     * An icon property should be specified to make it easier to identify a block.
     * These can be any of WordPress’ Dashicons, or a custom svg element.
     */
    icon: "align-wide",

    /**
     * Blocks are grouped into categories to help users browse and discover them.
     * The categories provided by core are `common`, `embed`, `formatting`, `layout` and `widgets`.
     */
    category: "widgets",

    /**
     * Optional block extended support features.
     */
    // supports: {
    //     // Removes support for an HTML mode.
    //     html: false
    // },
    attributes,

    /**
     * The edit function describes the structure of your block in the context of the editor.
     * This represents what the editor will render when the block is used.
     *
     * @see https://developer.wordpress.org/block-editor/developers/block-api/block-edit-save/#edit
     *
     * @param {Object} [props] Properties passed from the editor.
     *
     * @return {WPElement} Element to render.
     */
    edit,

    /**
     * The save function defines the way in which the different attributes should be combined
     * into the final markup, which is then serialized by the block editor into `post_content`.
     *
     * @see https://developer.wordpress.org/block-editor/developers/block-api/block-edit-save/#save
     *
     * @return {WPElement} Element to render.
     */
    save
});
